import React, { forwardRef, useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getSpreadDimensions, GUTTER_CONFIG } from '../../constants';
import ElementRenderer from './ElementRenderer';
import ImageLoadingPlaceholder from '../../elements/ImageLoadingPlaceholder';
import { useBook } from '../../../../contexts/BookContext';

/**
 * SpreadCanvas component for rendering and managing book spread elements
 */
const SpreadCanvas = forwardRef(
  (
    {
      elements = [],
      selectedElement,
      onSelectElement,
      onElementsChange,
      onElementDelete,
      onElementDuplicate,
      onZIndexChange,
      dimensions,
      paperColor,
      hasPageNumbers = false,
      currentSpread = 0,
      totalPages = 0,
      ensureOverlay,
      saveElements,
      generatingElementId,
    },
    ref
  ) => {
    // Track sorted elements in local state to ensure re-render on z-index changes
    const [sortedElements, setSortedElements] = useState([]);
    
    // Get loading state from BookContext
    const { currentBook, loadingUIMessage, showImageLoadingPlaceholder, processingStatus } = useBook();
    // Derived visibility: show while explicitly toggled on OR whenever processing is active
    // But hide if there are image elements since loading now shows inside them
    const placeholderVisible = useMemo(() => {
      const processingActive = !!(processingStatus && processingStatus !== 'completed' && processingStatus !== 'error' && processingStatus !== 'cancelled');
      const bookNotCompleted = !!(currentBook && currentBook.status && currentBook.status !== 'completed');
      let force = false;
      try { force = typeof window !== 'undefined' && !!window.__sbwForcePlaceholder; } catch (_) {}
      
      // Check if current spread has image elements
      const hasImageElements = elements && elements.some(el => el.type === 'image');
      
      // Hide full-page placeholder if there are image elements (loading shows inside them)
      const shouldHideForImageElements = hasImageElements;
      
      const visible = !!(showImageLoadingPlaceholder || processingActive || bookNotCompleted || force) && !shouldHideForImageElements;
      
      console.log('[SBW:SpreadCanvas] Placeholder visibility check:', {
        spreadId: currentSpread,
        showImageLoadingPlaceholder,
        processingStatus,
        processingActive,
        bookStatus: currentBook?.status,
        bookNotCompleted,
        windowForce: force,
        hasImageElements,
        shouldHideForImageElements,
        placeholderVisible: visible,
        timestamp: new Date().toISOString()
      });
      
      return visible;
    }, [showImageLoadingPlaceholder, processingStatus, currentBook, currentSpread, elements]);

    // Derive loading message directly from processingStatus so it always stays in sync
    const effectiveLoadingMessage = useMemo(() => {
      const statusMessages = {
        'pending':                    { progress: 5,   message: 'Please Wait...' },
        'pending2':                   { progress: 5,   message: 'Please Wait....' },
        'processing':                 { progress: 5,   message: 'Please Wait.....' },
        'savingTextElements':         { progress: 15,  message: 'Saving Text Elements...' },
        'updatingTextElements':       { progress: 16,  message: 'Updating Text Elements...' },
        'updatingTextElements2':      { progress: 17,  message: 'Updating Text Elements...' },
        'textElementsCreated':        { progress: 20,  message: 'Text Elements Created...' },
        'received':                   { progress: 30,  message: 'Request Received...' },
        'initializing':               { progress: 40,  message: 'Initializing image generation...' },
        'generatingCharacter':        { progress: 50,  message: 'Generating Character Details...' },
        'generatingImagePrompts':     { progress: 65,  message: 'Generating Image Prompts...' },
        'generatingCoverImage':       { progress: 80,  message: 'Generating Cover Image...' },
        'updatingImageElements':      { progress: 85,  message: 'Updating Image Elements...' },
        'updatedTextElementsInterior':{ progress: 85,  message: 'Finalizing Text Elements...' },
        'updatedCoverImage':          { progress: 90,  message: 'Finalizing Cover Image...' },
        'completed':                  { progress: 100, message: 'Complete!' },
      };
      // If processingStatus is set, ALWAYS use it (never fall back to stale loadingUIMessage)
      if (processingStatus && statusMessages[processingStatus]) {
        const result = statusMessages[processingStatus];
        console.log('[SpreadCanvas] effectiveLoadingMessage from processingStatus:', { processingStatus, result });
        return result;
      }
      // Only use loadingUIMessage as fallback when processingStatus is not set
      console.log('[SpreadCanvas] effectiveLoadingMessage from loadingUIMessage:', { processingStatus, loadingUIMessage });
      return loadingUIMessage;
    }, [processingStatus, loadingUIMessage]);
    
    if (!dimensions) {
      dimensions = getSpreadDimensions('us-trade');
    }

    // Debug when elements prop changes
    useEffect(() => {
      // Sort elements by z-index before rendering
      const elementsSortedByZIndex = [...(elements || [])].sort((a, b) => {
        // Simple parsing of z-index values
        const zIndexA = parseInt(a.zIndex || 0);
        const zIndexB = parseInt(b.zIndex || 0);
        
        return zIndexA - zIndexB;
      });
      
      const elementZIndices = elementsSortedByZIndex.map(el => ({
        id: el.id.substring(0, 8),
        zIndex: el.zIndex
      }));
      
      console.log('[SIMPLE] Elements sorted by z-index:', {
        count: elementsSortedByZIndex.length,
        indices: elementZIndices
      });
      
      setSortedElements(elementsSortedByZIndex);
    }, [elements, selectedElement]);

    const handleZIndexChange = useCallback((elementId, newZIndex) => {
      console.log('[SIMPLE] SpreadCanvas handling z-index change:', {
        elementId,
        newZIndex
      });
      
      onZIndexChange(elementId, parseInt(newZIndex));
    }, [onZIndexChange]);

    const handleSelectedElement = useCallback((elementId) => {
      console.log('[SIMPLE] Element selection requested:', elementId);
      
      // Verify the element exists
      const elementExists = elements && elements.some(el => el.id === elementId);
      
      if (elementExists) {
        const selectedElementData = elements.find(el => el.id === elementId);
        
        console.log('[SIMPLE] Element selected:', {
          id: elementId,
          type: selectedElementData.type,
          zIndex: selectedElementData.zIndex
        });
        
        onSelectElement(elementId);
      } else {
        console.warn('[SIMPLE] Element selection failed - element not found:', elementId);
        onSelectElement(null);
      }
    }, [elements, onSelectElement]);

    // Determine effective ensure overlay: use prop if provided; otherwise check global
    const effectiveEnsureOverlay = useMemo(() => {
      if (ensureOverlay) return ensureOverlay;
      try {
        if (typeof window !== 'undefined') return window.__sbwEnsureOverlay || null;
      } catch {}
      return null;
    }, [ensureOverlay]);

    // Center edit overlay state
    const [editRequestId, setEditRequestId] = useState(null);
    const [overlayPos, setOverlayPos] = useState({ top: -9999, left: -9999 });
    const [editingOpen, setEditingOpen] = useState(false);

    // Track position of selected element for center overlay (follows drag)
    useEffect(() => {
      let lastTop = overlayPos.top;
      let lastLeft = overlayPos.left;
      let rafId = null;
      let scheduled = false;

      const updateNow = () => {
        scheduled = false;
        if (!selectedElement || editingOpen) return;
        const el = document.querySelector(`[data-element-id="${selectedElement}"]`);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const nextTop = Math.round(rect.top + rect.height / 2);
        const nextLeft = Math.round(rect.left + rect.width / 2);
        if (nextTop !== lastTop || nextLeft !== lastLeft) {
          lastTop = nextTop;
          lastLeft = nextLeft;
          setOverlayPos({ top: nextTop, left: nextLeft });
          // Debug overlay position
          // eslint-disable-next-line no-console
          console.log('[CENTER PENCIL] overlayPos', { id: selectedElement, top: nextTop, left: nextLeft });
        }
      };

      const scheduleUpdate = () => {
        if (scheduled) return;
        scheduled = true;
        rafId = requestAnimationFrame(updateNow);
      };

      // initial computation
      scheduleUpdate();

      // Listen to common movement-related events
      window.addEventListener('resize', scheduleUpdate);
      window.addEventListener('scroll', scheduleUpdate, true);
      window.addEventListener('mousemove', scheduleUpdate, true);
      window.addEventListener('pointermove', scheduleUpdate, true);
      window.addEventListener('touchmove', scheduleUpdate, true);

      // Observe element size and attribute/style changes while dragging
      const el = document.querySelector(`[data-element-id="${selectedElement}"]`);
      let ro, mo;
      if (el && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(scheduleUpdate);
        ro.observe(el);
      }
      if (el && typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(scheduleUpdate);
        mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
      }

      return () => {
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('scroll', scheduleUpdate, true);
        window.removeEventListener('mousemove', scheduleUpdate, true);
        window.removeEventListener('pointermove', scheduleUpdate, true);
        window.removeEventListener('touchmove', scheduleUpdate, true);
        if (rafId) cancelAnimationFrame(rafId);
        if (ro) ro.disconnect();
        if (mo) mo.disconnect();
      };
    }, [selectedElement, editingOpen]);

    // Sync editing state so we can hide the center pencil when the editor is open
    useEffect(() => {
      const handler = (e) => {
        const { id, isOpen } = e.detail || {};
        if (id && id === selectedElement) setEditingOpen(!!isOpen);
      };
      window.addEventListener('sbw:editing-change', handler);
      // Initialize once on selection change
      if (typeof window !== 'undefined') {
        setEditingOpen(window.__sbwEditingSelectedId === selectedElement);
      }
      return () => window.removeEventListener('sbw:editing-change', handler);
    }, [selectedElement]);

    // Deselect when clicking anywhere outside the spread canvas
    useEffect(() => {
      const handleOutsideClick = (e) => {
        if (!ref?.current) return;

        // Respect suppression flag used by control buttons
        if (typeof window !== 'undefined' && window.__sbwSuppressCanvasClick) {
          window.__sbwSuppressCanvasClick = false;
          return;
        }

        const target = e.target;
        // Ignore clicks on control buttons or common overlay containers
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') ||
           target.closest('[data-role="control-button"]') ||
           target.closest('[role="dialog"]') ||
           target.closest('.Toastify') ||
           target.closest('[class*="Dialog"]') ||
           target.closest('[class*="dialog"]') ||
           target.closest('[data-selected-element-control-panel]') ||
           target.closest('[data-layers-panel]') ||
           target.closest('[data-element-context-menu]'))
        ) {
          return;
        }

        // Close any open context menu when clicking outside
        if (typeof window !== 'undefined' && window.__sbwCloseContextMenu) {
          window.__sbwCloseContextMenu();
        }

        if (!ref.current.contains(target)) {
          onSelectElement(null);
        }
      };

      // Use capture to get the event before it bubbles is stopped elsewhere
      document.addEventListener('click', handleOutsideClick, true);
      return () => document.removeEventListener('click', handleOutsideClick, true);
    }, [ref, onSelectElement]);

    const renderElements = () => {
      console.log('[SIMPLE] Rendering elements:', {
        elementCount: elements.length,
        selectedId: selectedElement,
        elements: sortedElements.map((el) => ({
          id: el.id.substring(0, 8),
          type: el.type,
          isSelected: el.id === selectedElement,
          zIndex: el.zIndex || 0,
        })),
      });

      // Render elements in the order of sortedElements (which is sorted by z-index in ASCENDING order)
      return sortedElements.map((element) => {
        const isSelected = element.id === selectedElement;

        return (
          <ElementRenderer
            key={`${element.id}-${element.zIndex || 0}-${element.src || ''}`}
            element={element}
            isSelected={isSelected}
            generatingElementId={generatingElementId}
            onSelect={() => {
              handleSelectedElement(element.id);
            }}
            onChange={(updates) => {
              onElementsChange(element.id, updates);
            }}
            onDelete={() => {
              console.log('DEBUG SpreadCanvas: Element deleted:', {
                id: element.id,
                type: element.type,
              });
              onElementDelete(element.id);
            }}
            onDuplicate={() => {
              console.log('DEBUG SpreadCanvas: Element duplicated:', {
                id: element.id,
                type: element.type,
              });
              onElementDuplicate(element.id);
            }}
            onZIndexChange={(newZIndex) => handleZIndexChange(element.id, newZIndex)}
            containerRef={ref}
            ensureOverlay={effectiveEnsureOverlay}
            saveElements={saveElements}
            editRequestId={editRequestId}
            onConsumeEditRequest={() => setEditRequestId(null)}
          />
        );
      });
    };

    // Determine if this is a landscape orientation
    const isLandscape = dimensions.orientation === 'landscape';
    
    // For landscape-custom size (Lulu cover), we need specific dimensions
    // to match the PDF output exactly (12.382 × 9.25 inches)
    const canvasStyle = isLandscape ? {
      // For landscape orientation, use the exact dimensions that match the PDF output
      width: dimensions.width,
      height: dimensions.height,
      backgroundColor: paperColor,
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
      overflow: 'hidden',
    } : {
      // For portrait orientation, use the standard doubled width
      width: dimensions.width * 2,
      height: dimensions.height,
      backgroundColor: paperColor,
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
      overflow: 'hidden',
    };
    
    const handleCanvasClick = (e) => {
      if (!ref?.current) return;

      // If a control button just handled this interaction, skip canvas logic
      // entirely to avoid deselecting or re-selecting.
      if (typeof window !== 'undefined' && window.__sbwSuppressCanvasClick) {
        window.__sbwSuppressCanvasClick = false;
        return;
      }

      // If the click originated from a modal or the SelectedElementControlPanel, ignore it
      const target = e.target;
      if (
        target &&
        typeof target.closest === 'function' &&
        (target.closest('[class*="Dialog"]') ||
         target.closest('[class*="dialog"]') ||
         target.closest('[role="dialog"]') ||
         target.closest('[data-selected-element-control-panel]') ||
         target.closest('[data-layers-panel]'))
      ) {
        return;
      }

      // If the click started on a UI control button (duplicate/delete/edit),
      // don't run hit-testing or change selection at the canvas level.
      if (
        target &&
        typeof target.closest === 'function' &&
        (target.closest('button') || target.closest('[data-role="control-button"]'))
      ) {
        return;
      }

      // If the click occurred within an element container (identified by
      // data-element-id), directly select that element regardless of where the
      // button is positioned relative to its visual bounds.
      const elementRoot = target && typeof target.closest === 'function'
        ? target.closest('[data-element-id]')
        : null;
      const elementIdFromDom = elementRoot?.getAttribute?.('data-element-id');
      if (elementIdFromDom) {
        handleSelectedElement(elementIdFromDom);
        return;
      }

      const rect = ref.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Walk elements from topmost to bottom-most (highest z-index last in sortedElements)
      const hit = [...sortedElements]
        .sort((a, b) => (parseInt(a.zIndex || 0) - parseInt(b.zIndex || 0)))
        .reduce((acc, el) => {
          if (acc) return acc;
          const pos = el.position || { x: 0, y: 0 };
          const size = el.size || { width: 0, height: 0 };
          const withinX = clickX >= pos.x && clickX <= pos.x + size.width;
          const withinY = clickY >= pos.y && clickY <= pos.y + size.height;
          if (withinX && withinY) {
            return el;
          }
          return null;
        }, null);

      if (hit) {
        console.log('[SIMPLE] Canvas hit-test selected element:', {
          id: hit.id,
          type: hit.type,
          zIndex: hit.zIndex,
        });
        handleSelectedElement(hit.id);
      } else {
        console.log('DEBUG SpreadCanvas: Canvas clicked, deselecting element');
        handleSelectedElement(null);
      }
    };

    return (
      <div
        ref={ref}
        className="relative"
        style={canvasStyle}
        onClick={handleCanvasClick}
      >
        {/* Center guide - positioned differently for landscape vs portrait */}
        {!isLandscape && (
          <div
            className="absolute inset-y-0 left-1/2 border-l border-dashed border-gray-300"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* For landscape, especially custom size, we don't need a center guide 
            since it's a single page view rather than a spread */}

        {/* Elements */}
        {renderElements()}
        
        {/* Image Loading Placeholder: handle portrait spreads and landscape cover correctly */}
        {placeholderVisible && (() => {
          // For portrait spreads, fill the entire right page
          // For landscape (cover), center on the front cover
          const size = isLandscape 
            ? { width: 500, height: 667 }
            : { width: dimensions.width, height: dimensions.height };
          const position = isLandscape
            ? { x: Math.max(0, Math.round(dimensions.width * 0.75 - 250)), y: 135 }
            : { x: dimensions.width, y: 0 };
          console.log('[SpreadCanvas] Rendering ImageLoadingPlaceholder with:', {
            message: effectiveLoadingMessage?.message,
            progress: effectiveLoadingMessage?.progress,
            currentStatus: processingStatus,
            effectiveLoadingMessage
          });
          return (
            <ImageLoadingPlaceholder 
              message={effectiveLoadingMessage?.message || "Generating image..."}
              progress={effectiveLoadingMessage?.progress || 0}
              currentStatus={processingStatus}
              size={size}
              position={position}
            />
          );
        })()}

        {/* Page Numbers */}
        {hasPageNumbers && currentSpread > 0 && (
          <>
            {/* Left page number - centered on left half */}
            {!isLandscape && (
              <div
                className="absolute bottom-6 text-sm text-gray-600 font-medium"
                style={{
                  left: '25%',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1000,
                }}
              >
                {currentSpread * 2 - 1}
              </div>
            )}
            
            {/* Right page number - centered on right half */}
            <div
              className="absolute bottom-6 text-sm text-gray-600 font-medium"
              style={{
                left: isLandscape ? '50%' : '75%',
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            >
              {isLandscape ? currentSpread : currentSpread * 2}
            </div>
          </>
        )}

        {/* Center Edit Overlay (fixed within document) */}
        {selectedElement && !editingOpen && (elements.find(el => el.id === selectedElement)?.type !== 'image' && elements.find(el => el.id === selectedElement)?.type !== 'text' && elements.find(el => el.id === selectedElement)?.type !== 'shape') && (
          <button
            type="button"
            data-role="control-button edit-center"
            style={{
              position: 'fixed',
              top: overlayPos.top,
              left: overlayPos.left,
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
            }}
            className="p-4 bg-white rounded-full shadow-lg ring-2 ring-indigo-500 hover:bg-white"
            onPointerDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); if (typeof window !== 'undefined') window.__sbwSuppressCanvasClick = true; }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof window !== 'undefined') window.__sbwSuppressCanvasClick = true;
              if (typeof window !== 'undefined') {
                // eslint-disable-next-line no-console
                console.log('[CENTER PENCIL] click -> request edit', selectedElement);
                window.__sbwEditRequestId = selectedElement;
                window.dispatchEvent(new CustomEvent('sbw:request-edit', { detail: { id: selectedElement } }));
              }
              setEditRequestId(selectedElement);
            }}
            title="Edit element"
          >
            {/* simple pencil */}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 013.182 3.182L7.125 19.588 3 21l1.412-4.125L16.862 3.487z" />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

// Define PropTypes to help TypeScript understand component props
SpreadCanvas.propTypes = {
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number
      }),
      size: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number
      }),
      zIndex: PropTypes.number,
      content: PropTypes.string,
      imagePrompt: PropTypes.string,
      src: PropTypes.string,
    })
  ),
  selectedElement: PropTypes.string,
  onSelectElement: PropTypes.func.isRequired,
  onElementsChange: PropTypes.func.isRequired,
  onElementDelete: PropTypes.func.isRequired,
  onElementDuplicate: PropTypes.func.isRequired,
  onZIndexChange: PropTypes.func.isRequired,
  dimensions: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  paperColor: PropTypes.string,
  hasPageNumbers: PropTypes.bool,
  currentSpread: PropTypes.number,
  totalPages: PropTypes.number
};

// Add display name for better debugging
SpreadCanvas.displayName = 'SpreadCanvas';
export default SpreadCanvas;
